import {
  createAction,
  ListenerEffectAPI,
  UnknownAction
} from '@reduxjs/toolkit'

import { startAppListening } from '@/config/store/listenerMiddleware'
import { selectFlags } from '@/ducks/app/selectors/app.selectors'
import { startInitialization } from '@/ducks/app/slices/app.shared'
import { selectToken } from '@/ducks/auth/auth.selectors'
import {
  WSNewOrderMessage,
  WSRejectedOrderMessage,
  WSStoreStatusMessage
} from '@/ducks/events/events.types'
import socketSingleton, {
  SOCKET_EVENTS
} from '@/ducks/events/websocket/events.ws'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { RootState } from '@/ducks/root.reducer'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { createStatusBrowserNotification } from '../utils/events.utils'

const SLICE_NAME = 'events'

const eventActions = {
  [SOCKET_EVENTS.NEW_ORDER]: createAction<WSNewOrderMessage>(
    `${SLICE_NAME}/${SOCKET_EVENTS.NEW_ORDER}`
  ),
  [SOCKET_EVENTS.CONNECT]: createAction(
    `${SLICE_NAME}/${SOCKET_EVENTS.CONNECT}`
  ),
  [SOCKET_EVENTS.CONNECT_ERROR]: createAction(
    `${SLICE_NAME}/${SOCKET_EVENTS.CONNECT_ERROR}`
  ),
  [SOCKET_EVENTS.REJECTED_ORDER]: createAction<WSRejectedOrderMessage>(
    `${SLICE_NAME}/${SOCKET_EVENTS.REJECTED_ORDER}`
  ),
  [SOCKET_EVENTS.BUSY_STATUS]: createAction<WSStoreStatusMessage>(
    `${SLICE_NAME}/${SOCKET_EVENTS.BUSY_STATUS}`
  )
}

const connectSocket = async (
  _: UnknownAction,
  listenerApi: ListenerEffectAPI<RootState, any>
) => {
  const state = listenerApi.getState()
  const token = selectToken(state)
  const socketUrl = selectFlags(state).lmd_revamp_store_web_App_new_socket
    ? 'store'
    : 'chat'

  if (!token) {
    socketSingleton.disconnect()
  } else {
    socketSingleton.setEventHandler((event, data) => {
      listenerApi.dispatch(eventActions[event](data))
    })
    socketSingleton.connect(token, socketUrl)
  }
}

// One connect path: a token change (login/logout) or a boot. Two separate
// listeners meant every login disconnected and reconnected the socket twice.
startAppListening({
  predicate: (
    action: UnknownAction,
    currentState: RootState,
    previousState: RootState
  ) =>
    selectToken(currentState) !== selectToken(previousState) ||
    startInitialization.match(action),
  effect: (...args) => connectSocket(...args)
})

startAppListening({
  actionCreator: eventActions.store_status,
  effect: async (action, listenerApi) => {
    const statusMessage = action.payload as WSStoreStatusMessage

    const currentStoreId = selectStoreId(listenerApi.getState())
    // only show notification if it's for this store
    if (statusMessage.storeId !== currentStoreId) return

    if (statusMessage.isBusy) {
      createStatusBrowserNotification({
        title: 'Store status changed',
        message: 'Your store has been set to busy mode and will be back online soon'
      })

      listenerApi.dispatch(
        addSystemNotification({
          type: 'system',
          intent: 'warning',
          message: 'Your store has been set to busy mode and will be back online soon',
          duration: 10000 // 10 seconds for busy notifications
        })
      )
    } else {
      const backOnlineMessage =
        'Your store is back online and ready to accept orders!'

      createStatusBrowserNotification({
        title: 'Store status changed',
        message: backOnlineMessage
      })

      listenerApi.dispatch(
        addSystemNotification({
          type: 'system',
          intent: 'success',
          message: backOnlineMessage,
          duration: 8000 // 8 seconds for back online notifications
        })
      )
    }
  }
})

export { eventActions }
